import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, GuestUser, LoginRequest, RegisterRequest, GuestSessionRequest } from '../../types/auth';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  guestUser: GuestUser | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

// Helper functions to persist user data
const getUserFromStorage = (): User | null => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

const getGuestUserFromStorage = (): GuestUser | null => {
  const guestStr = localStorage.getItem('guestUser');
  return guestStr ? JSON.parse(guestStr) : null;
};

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('token'),
  user: getUserFromStorage(),
  guestUser: getGuestUserFromStorage(),
  token: localStorage.getItem('token'),
  isLoading: false,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      const data = await response.json();

      console.log('=== AUTHSLICE LOGIN DATA ===');
      console.log('Response data:', JSON.stringify(data, null, 2));
      console.log('User being stored:', JSON.stringify(data.user, null, 2));
      console.log('Token:', data.token);
      console.log('===========================');

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      console.log('=== LOCALSTORAGE AFTER LOGIN ===');
      console.log('Stored user:', localStorage.getItem('user'));
      console.log('Stored token:', localStorage.getItem('token'));
      console.log('================================');

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      // Add company parameter to registration data
      const company = process.env.REACT_APP_COMPANY || 'QASTCO';
      const userDataWithCompany = {
        ...userData,
        company
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userDataWithCompany),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      const data = await response.json();
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createGuestSession = createAsyncThunk(
  'auth/createGuestSession',
  async (guestData: GuestSessionRequest, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/guest-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(guestData),
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error.message);
      }

      const data = await response.json();
      localStorage.setItem('guestToken', data.token);
      localStorage.setItem('guestUser', JSON.stringify(data.guest));
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.guestUser = null;
      state.token = null;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('guestToken');
      localStorage.removeItem('guestUser');
    },
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        console.log('=== REDUX STATE UPDATE ===');
        console.log('Action payload:', JSON.stringify(action.payload, null, 2));
        console.log('User being set in state:', JSON.stringify(action.payload.user, null, 2));
        console.log('=========================');

        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;

        console.log('=== REDUX STATE AFTER UPDATE ===');
        console.log('State user:', JSON.stringify(state.user, null, 2));
        console.log('State isAuthenticated:', state.isAuthenticated);
        console.log('===============================');
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Guest session
      .addCase(createGuestSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGuestSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.guestUser = action.payload.guest;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(createGuestSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, setToken } = authSlice.actions;
export default authSlice.reducer;